import { create } from 'zustand';

import {
  partnerLoginAPI,
  changePasswordAPI,
  logoutAPI,
  switchBranchAPI,
  verifyPortalOtpAPI,
  resendPortalOtpAPI,
} from 'Api/portalAuthAPI';
import { onSessionExpired, onSessionRefreshed } from 'Api/portalClient';
import {
  savePortalSession,
  loadPortalSession,
  clearPortalSession,
} from 'helpers/portalSession';
import { logSessionIdentity } from 'helpers/sessionIdentityLog';
import { clearBrandingCache, resetBrandingLoad, DEFAULT_PORTAL_BRANDING } from '../theme/brandingSource';
import useThemeStore from './useThemeStore';
import useWalletStore from './useWalletStore';
import { permissionChecker } from 'helpers/permissions';
import { GA_LABEL_ENUMS, triggerGAEvent } from '../analytics/GAEvents';

const PORTAL = 'partner';

/**
 * SMB-3859 verification probe — TEMPORARY, remove once the ticket is signed off.
 *
 * Dumps EVERYTHING the partner login returned, read back from the SAVED session
 * rather than from the raw response, so what it prints is what the rest of the
 * portal actually sees after a reload — not just what arrived on the wire.
 *
 * Three views, because they answer different questions:
 *   1. the full session object, so any field the ticket did not think to ask for
 *      is visible too;
 *   2. a flattened key list, so "is X in the response, and under what name" is a
 *      text search rather than a tree expansion — the ticket's Notes ask for the
 *      exact key names, and this is that list;
 *   3. the six the ticket names, with a pass/fail line.
 *
 * Credentials are redacted. `jwtToken` and `refreshToken` are live credentials
 * for the rest of the session, and this log will end up pasted into a ticket —
 * so their LENGTH is printed (enough to prove they arrived) and never the value.
 */
const REDACTED_KEYS = ['jwtToken', 'refreshToken', 'password', 'accessJWT'];

/**
 * A deep copy with the credentials replaced by their length.
 *
 * Logging the session object directly would print the live `jwtToken` and
 * `refreshToken` in full — and the refresh token is the long-lived half, good
 * until someone signs in again. Since the whole purpose of this probe is to be
 * screenshotted into a ticket, the raw object must never be the thing that gets
 * logged.
 */
const redactedCopy = (value) => {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(redactedCopy);
  return Object.fromEntries(
    Object.entries(value).map(([k, v]) => [
      k,
      REDACTED_KEYS.includes(k)
        ? `<redacted, ${typeof v === 'string' ? v.length : '?'} chars>`
        : redactedCopy(v),
    ]),
  );
};

/** `{a:{b:1}}` → `{'a.b': 1}`, so every leaf key is greppable in one flat list. */
const flatten = (value, prefix = '', out = {}) => {
  if (value === null || typeof value !== 'object') {
    out[prefix] = value;
    return out;
  }
  if (Array.isArray(value)) {
    // Arrays are summarised, not expanded: `permissions` alone is ~74 strings and
    // would bury everything else. The full array is still in view (1) above.
    out[`${prefix} []`] = `Array(${value.length})${value.length ? ` e.g. ${JSON.stringify(value[0])}` : ''}`;
    return out;
  }
  Object.entries(value).forEach(([k, v]) => {
    const path = prefix ? `${prefix}.${k}` : k;
    if (REDACTED_KEYS.includes(k)) {
      out[path] = typeof v === 'string' ? `<redacted, ${v.length} chars>` : '<redacted>';
      return;
    }
    flatten(v, path, out);
  });
  return out;
};

const logSessionFields = (label) => {
  const s = loadPortalSession(PORTAL) || {};

  console.groupCollapsed(`[SMB-3859] ${label} — partner login response`);

  // 1. Everything, as it is stored — minus the two live credentials.
  console.log('full session object:', redactedCopy(s));

  // 2. Every leaf key, flattened. `console.table` renders it as a two-column
  //    grid that can be copied straight into the ticket.
  const flat = flatten(s);
  console.log(`flattened keys (${Object.keys(flat).length}):`);
  console.table(
    Object.entries(flat).map(([key, value]) => ({
      key,
      type: value === null ? 'null' : typeof value,
      value: typeof value === 'string' && value.length > 80 ? `${value.slice(0, 80)}…` : value,
    })),
  );

  // 3. The six the ticket asks for.
  //
  //    Two ids are shown deliberately. `user.id` is the `partner_users` row — the
  //    person — while `travellerIdentity.defaultUserId` is the `user` row search
  //    and booking actually run under, and it is that second one the ticket means
  //    by "the identifier already used as userId in search requests".
  //
  //    Channel is shown from both places for the same reason: `partner.channel`
  //    is lowercased by the server's toApiChannel ('b2b'), while
  //    `travellerIdentity.channel` is not ('B2B').
  console.log('the six SMB-3859 asks for:', {
    name: s.user?.name,
    userId_partnerUsers: s.user?.id,
    userId_forSearch: s.travellerIdentity?.defaultUserId,
    email: s.user?.email,
    partnerCode: s.partner?.partnerCode,
    role: s.role?.name,
    roleCode: s.role?.code,
    channel_partner: s.partner?.channel,
    channel_travellerIdentity: s.travellerIdentity?.channel,
  });

  // Anything falsy here is a field the ticket asks for that did NOT arrive.
  const missing = [
    ['name', s.user?.name],
    ['userId', s.user?.id],
    ['email', s.user?.email],
    ['partnerCode', s.partner?.partnerCode],
    ['role', s.role?.name],
    ['channel', s.partner?.channel],
  ]
    .filter(([, v]) => !v)
    .map(([k]) => k);
  if (missing.length) console.warn('[SMB-3859] MISSING from login response:', missing);
  else console.log('[SMB-3859] all six fields present ✅');

  console.groupEnd();
};

/**
 * Partner portal authentication.
 *
 * Signs in against POST /app/v1/partner/auth/login, which authenticates
 * `partner_users` and admits PARTNER accounts only. Zenvoya's own staff are
 * refused here by the server and belong on the CRM portal — nothing in the
 * request selects the portal, so that decision is made server-side from the
 * owning partner's type and cannot be spoofed from the client.
 *
 * Seeded from sessionStorage so a refresh does not sign the user out. The
 * previous implementation ignored the password entirely and synthesised a user
 * from the email string.
 */

const fromSession = (session) => ({
  isAuthenticated: !!session?.jwtToken,
  user: session?.user || null,
  partner: session?.partner || null,
  branches: session?.branches || [],
  role: session?.role || null,
  permissions: session?.permissions || [],
  modules: session?.modules || [],
  // What the tenant sells (Title-Case plurals per the session:
  // "Activities" / "Flights" / "Hotels"). Consumers normalise the casing
  // themselves. `savePortalSession` already persists this field, so a
  // reload preserves it — the projection here is what exposes it via the
  // store selector.
  verticals: session?.verticals || [],
  /**
   * The branch this session is working at, or null for the partner's default.
   * Set by `switchBranch` below; persisted by `savePortalSession` so it survives
   * a reload and can be re-applied after a token refresh.
   */
  actingBranchCode: session?.actingBranchCode ?? null,
  perms: permissionChecker(session?.permissions || []),
  mustChangePassword: !!session?.user?.mustChangePassword,
  accessJWT: session?.jwtToken || '',
  /**
   * Which `user` row the embedded traveller app books under:
   * `{ partnerUserId, agentUserId, channel, defaultUserId }`.
   *
   * A portal token authenticates against the traveller API but its
   * `partner_users` id resolves to no `user` row — and that fails SILENTLY
   * (400 with `data: []`, which the traveller app reads as a logged-in user
   * with `id: undefined`). This is the id that actually resolves.
   */
  travellerIdentity: session?.travellerIdentity || null,
});

const useAuthStore = create((set, get) => ({
  ...fromSession(loadPortalSession(PORTAL)),
  loggingIn: false,
  /**
   * The pre-auth token from MFA step one, held in MEMORY only.
   *
   * Deliberately not persisted: it lives ten minutes, is good for exactly two
   * routes, and a reload should drop the half-finished sign-in back to the start
   * rather than resume it from storage.
   */
  preAuthToken: '',
  mfaEmail: '',
  /**
   * The password just used to sign in, held in MEMORY only, so the
   * change-password screen can rotate it without asking the user to retype
   * what they typed one screen ago. Cleared the moment it is no longer
   * needed — once rotation completes, or once login resolves without
   * `mustChangePassword`. A reload loses it like it loses `preAuthToken`;
   * `ChangePasswordPage` falls back to asking for a fresh sign-in rather than
   * silently blocking.
   */
  pendingPassword: '',

  login: async (email, password, partnerCode) => {
    // Trim BEFORE the blank check: an address of nothing but spaces is truthy,
    // so checking first would send an empty email for the server to reject
    // instead of telling the user the field is empty.
    const trimmedEmail = String(email ?? '').trim();
    if (!trimmedEmail || !password) {
      return { ok: false, message: 'Enter your email and password.' };
    }
    if (!String(partnerCode ?? '').trim()) {
      return { ok: false, message: 'Enter your partner code.' };
    }

    set({ loggingIn: true });
    try {
      const result = await partnerLoginAPI(trimmedEmail, password, partnerCode);
      if (!result.ok) {
        triggerGAEvent(GA_LABEL_ENUMS.partner_login_failure, result.message);
        return result;
      }

      /**
       * With MFA on, step one returns a pre-auth token and NO session. Nothing is
       * saved here — `savePortalSession` must not see a half-authenticated state,
       * or a reload would present the portal as signed in.
       */
      if (result.data?.mfaRequired) {
        set({ preAuthToken: result.data.jwtToken, mfaEmail: result.data.email || '', pendingPassword: password });
        triggerGAEvent(GA_LABEL_ENUMS.partner_login_mfa_required);
        return { ok: true, mfaRequired: true, email: result.data.email, expiresInMinutes: result.data.expiresInMinutes };
      }

      savePortalSession(PORTAL, result.data);
      logSessionFields('password login'); // SMB-3859 probe — remove after sign-off
      logSessionIdentity(PORTAL, 'login');
      const mustChangePassword = !!result.data?.user?.mustChangePassword;
      set({ ...fromSession(loadPortalSession(PORTAL)), pendingPassword: mustChangePassword ? password : '' });
      // After the save, so the event carries the new session's partner_id.
      triggerGAEvent(GA_LABEL_ENUMS.partner_login_success, '', '', '', {
        login_method: 'password',
        must_change_password: mustChangePassword,
      });
      return { ok: true, mustChangePassword };
    } finally {
      set({ loggingIn: false });
    }
  },

  /** MFA step two. On success this is the point the session first exists. */
  verifyOtp: async (otp) => {
    const preAuthToken = get().preAuthToken;
    if (!preAuthToken) {
      return { ok: false, message: 'Start again from the sign-in screen.' };
    }
    if (!/^\d{6}$/.test(String(otp || '').trim())) {
      return { ok: false, message: 'Enter the 6-digit code.' };
    }

    set({ loggingIn: true });
    try {
      const result = await verifyPortalOtpAPI(PORTAL, preAuthToken, otp);
      if (!result.ok) {
        triggerGAEvent(GA_LABEL_ENUMS.partner_login_otp_failure, result.message);
        return result;
      }

      savePortalSession(PORTAL, result.data);
      logSessionFields('MFA verify'); // SMB-3859 probe — remove after sign-off
      logSessionIdentity(PORTAL, 'MFA verify');
      const mustChangePassword = !!result.data?.user?.mustChangePassword;
      // Clear the pre-auth token: it is spent, and keeping it would leave a
      // half-authenticated credential in memory for the rest of the session.
      // `pendingPassword` (captured at step one) only survives if rotation
      // still needs it.
      set({
        ...fromSession(loadPortalSession(PORTAL)),
        preAuthToken: '',
        mfaEmail: '',
        pendingPassword: mustChangePassword ? get().pendingPassword : '',
      });
      triggerGAEvent(GA_LABEL_ENUMS.partner_login_success, '', '', '', {
        login_method: 'mfa',
        must_change_password: mustChangePassword,
      });
      return { ok: true, mustChangePassword };
    } finally {
      set({ loggingIn: false });
    }
  },

  /** Send another code. The previous one stops working. */
  resendOtp: async () => {
    const preAuthToken = get().preAuthToken;
    if (!preAuthToken) return { ok: false, message: 'Start again from the sign-in screen.' };
    triggerGAEvent(GA_LABEL_ENUMS.partner_login_otp_resend);
    return await resendPortalOtpAPI(PORTAL, preAuthToken);
  },

  /** Abandon a half-finished sign-in (the code screen's "back"). */
  cancelMfa: () => set({ preAuthToken: '', mfaEmail: '' }),

  /**
   * First-login rotation. No new token is issued, so only the flag clears.
   *
   * `currentPassword` comes from `pendingPassword` — captured at sign-in —
   * rather than the screen asking for it again. Missing it means the tab was
   * reloaded (memory-only state), so there is no way to complete the
   * rotation without asking the user to type the temporary password again;
   * the honest answer is to have them sign in again instead.
   */
  changePassword: async (newPassword) => {
    const token = get().accessJWT;
    const currentPassword = get().pendingPassword;
    if (!token || !currentPassword) {
      return { ok: false, message: 'Sign in again to continue.', needsReauth: true };
    }

    const result = await changePasswordAPI(token, currentPassword, newPassword);
    if (!result.ok) {
      triggerGAEvent(GA_LABEL_ENUMS.partner_password_change_failure, result.message);
      return result;
    }

    const session = loadPortalSession(PORTAL);
    const next = { ...session, user: { ...session?.user, mustChangePassword: false } };
    savePortalSession(PORTAL, next);
    set({ ...fromSession(next), pendingPassword: '' });
    triggerGAEvent(GA_LABEL_ENUMS.partner_password_change_success);
    return { ok: true };
  },

  /**
   * Work at a different branch of this agency.
   *
   * The choice has to reach the engines, and for a partner session the only
   * carrier they trust is the TOKEN — `x-acting-branch-code` is honoured for CRM
   * sessions only, by design. So this exchanges the access token for one whose
   * `branch_code` claim is the chosen branch; from here every request is scoped
   * to it, including the embedded traveller app, which is handed the same token.
   *
   * The refresh token is NOT rotated — only the short-lived access token is
   * replaced — so one-session-per-user and replay detection are untouched.
   *
   * The choice is remembered locally because the server stores nothing: see
   * `reapplyBranch`, which puts it back after a refresh has re-minted from the
   * user row.
   */
  switchBranch: async (branchCode) => {
    const token = get().accessJWT;
    if (!token) return { ok: false, message: 'Sign in again to change branch.' };
    if (String(branchCode) === String(get().actingBranchCode || '')) return { ok: true };

    const result = await switchBranchAPI(token, branchCode);
    if (!result.ok) return result;

    const session = loadPortalSession(PORTAL);
    const next = {
      ...session,
      jwtToken: result.data?.jwtToken || session?.jwtToken,
      accessTokenExpiresAt: result.data?.accessTokenExpiresAt || session?.accessTokenExpiresAt,
      actingBranchCode: result.data?.branchCode ?? Number(branchCode),
    };
    savePortalSession(PORTAL, next);
    set({ ...fromSession(next) });
    // Every request from here scopes to a different branch, so the identity that
    // was printed at sign-in is now stale — reprint it rather than leave the
    // console asserting a branch this session has moved off.
    logSessionIdentity(PORTAL, 'branch switch');
    triggerGAEvent(GA_LABEL_ENUMS.partner_branch_switch, '', '', '', { branch_code: String(branchCode) });
    // The wallet is denominated in the BRANCH's currency — `getPartnerCurrency`
    // resolves through `actingBranchCode` — so the figure just went stale, and a
    // stale balance under the new branch's label is the one thing a money
    // readout must never show. Re-read rather than clear: the header chip and
    // the wallet page both load once on mount and neither remounts on a switch,
    // so nothing would ask again. `canTopUp` is re-derived by the same call,
    // which is what withdraws Add money when the new branch is not INR.
    useWalletStore.getState().refresh();
    return { ok: true };
  },

  /**
   * Put the chosen branch back into a token that has just been re-minted.
   *
   * `/partner/auth/refresh` re-reads the user row, which knows nothing about
   * this choice, so every refresh silently returns the operator to the partner's
   * default branch. Called by the transport after a successful partner refresh.
   *
   * Failure is deliberately quiet: the session is valid either way, just scoped
   * to the default branch, and a toast about a background token exchange the
   * operator never asked for would explain nothing.
   */
  reapplyBranch: async () => {
    const branchCode = loadPortalSession(PORTAL)?.actingBranchCode;
    if (!branchCode) return;
    const token = loadPortalSession(PORTAL)?.jwtToken;
    if (!token) return;
    const result = await switchBranchAPI(token, branchCode);
    if (!result.ok) return;
    const session = loadPortalSession(PORTAL);
    const next = {
      ...session,
      jwtToken: result.data?.jwtToken || session?.jwtToken,
      accessTokenExpiresAt: result.data?.accessTokenExpiresAt || session?.accessTokenExpiresAt,
    };
    savePortalSession(PORTAL, next);
    set({ ...fromSession(next) });
  },

  /**
   * Sign out. Revokes the refresh family SERVER-side first (S-204) — clearing
   * local storage alone would leave the session usable by anyone holding the
   * refresh token. The local clear runs regardless, so a network failure cannot
   * trap the user in a signed-in shell.
   */
  logout: async () => {
    // Before anything is cleared, so the event still carries partner_id.
    triggerGAEvent(GA_LABEL_ENUMS.partner_logout);
    const refreshToken = loadPortalSession(PORTAL)?.refreshToken;
    if (refreshToken) {
      try {
        await logoutAPI(PORTAL, refreshToken);
      } catch {
        /* best effort */
      }
    }
    clearPortalSession(PORTAL);
    // Drop this tab's cached brand too. It is keyed by partnerCode so a
    // different partner could not have inherited it anyway, but the SAME
    // partner signing back in should re-read §30.1 rather than paint from a
    // cache written before whatever they changed in between.
    clearBrandingCache();
    resetBrandingLoad();
    // ...and the brand already painted in memory (SMB-4204): without this the
    // next sign-in in this tab showed the previous session's colours until
    // §30.1 answered.
    useThemeStore.getState().setBranding(DEFAULT_PORTAL_BRANDING);
    // Same reasoning as the brand cache above, and it matters more: the wallet
    // store is module-level and outlives this call, so without it the next
    // partner to sign in on this tab inherits the previous one's balance —
    // relabelled into their own currency, because that comes from the session.
    useWalletStore.getState().reset();
    set({ ...fromSession(null), pendingPassword: '' });
  },
}));

/**
 * The transport signs out a session the server has rejected, and this is how the
 * store hears about it. Only the in-memory copy is dropped — the stored one is
 * already gone by the time this runs, and there is no refresh family left to
 * revoke — which flips `isAuthenticated` and lets the route guard redirect.
 *
 * Registered at module scope, not in a component: a 401 can land while no screen
 * that would have set it up is mounted.
 */
onSessionExpired(PORTAL, () => useAuthStore.setState(fromSession(null)));

/**
 * A refresh rotated the tokens in storage; re-read them so `accessJWT` stops being
 * the superseded one. The refresh replaces only the token pair, so re-deriving
 * state from the stored session is enough — and `fromSession` reads that session
 * whole, so the cached user/partner/permissions survive untouched.
 *
 * Registered at module scope for the same reason as the handler above: a rotation
 * can land while no screen that would have set it up is mounted.
 */
onSessionRefreshed(PORTAL, () => {
  useAuthStore.setState(fromSession(loadPortalSession(PORTAL)));
  /**
   * A refresh re-mints from the USER ROW, which knows nothing about the branch
   * this session chose — so without this every refresh quietly returns the
   * operator to the partner's default branch, mid-session, and the next booking
   * is made somewhere they did not pick. Re-applied here rather than inside the
   * transport because this store is what owns the remembered choice.
   */
  useAuthStore.getState().reapplyBranch();
});

/**
 * SMB-3859 verification probe — TEMPORARY, remove with the rest of the probe.
 *
 * The two call sites above only fire at the moment of sign-in, which would mean
 * signing out and back in to see anything. This runs on module load — so a plain
 * reload of an already-signed-in tab prints the same report — and hangs the same
 * function off `window.__smb3859()` so it can be re-run from devtools at any
 * point in the session (e.g. after a token refresh, to confirm the fields
 * survive rotation rather than only existing on the login response).
 */
if (typeof window !== 'undefined') {
  window.__smb3859 = () => logSessionFields('manual');
  if (loadPortalSession(PORTAL)?.jwtToken) logSessionFields('rehydrated session');
  if (loadPortalSession(PORTAL)?.jwtToken) logSessionIdentity(PORTAL, 'rehydrated session');
}

export default useAuthStore;
